import type { AppointmentView, ConversationView, InventoryItem, InventoryShelf, PhoneEventView, SecurityResponse, StaffListItem } from "@vaulted/shared";
import { STATUS_LABELS, formatVials, isOpenStatus, perVial } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { get } from "../api";
import { FormsBadge, STATUS_TONE, time12 } from "../components/AppointmentDialog";
import { GfeBadge } from "../components/Gfe";
import { Badge, Loading, Panel, cx } from "../components/ui";
import { useSession } from "../session";
import { addDays, todayIn, useCalendar } from "./Calendar";
import { displayName } from "./Clients";
import { useWaitingRequests, whenRequested } from "./MobileRequests";
import { usePendingCharts } from "./PendingCharts";

function greeting(tz: string) {
  const hour = Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: tz }).format(new Date()));
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

const kindTab = (i: InventoryItem) => (i.kind === "medication" ? "medications" : i.kind === "fluid" ? "fluids" : "supplies");
const longDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const dayHeading = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const shortDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const MINUTE = 60_000;

/**
 * Today: what each person needs to see, as their permissions allow. Today's visits; charts to sign
 * or review; mobile requests and unconfirmed visits for tomorrow; unanswered texts and calls; stock
 * that's low, expired, or expiring. Sections with nothing in them are left out.
 */
export function TodayPage() {
  const { me, can } = useSession();
  const tz = me?.clinic.timezone;
  const today = todayIn(tz);
  const tomorrow = addDays(today, 1);
  const booking = can("appointments.manage");
  // Managers see everyone's visits today; everyone else sees only their own, a week ahead.
  const everyoneToday = can("today.view_all");

  const day = useCalendar(today, today, booking && everyoneToday);
  const next = useCalendar(tomorrow, tomorrow, booking);
  const week = useCalendar(today, addDays(today, 6), booking && !everyoneToday);
  // Event shifts they're working, a month ahead.
  const month = useCalendar(today, addDays(today, 29), booking, 5 * MINUTE);
  const charts = usePendingCharts().data;
  const unsigned = charts?.mine ?? [];
  const toReview = charts?.toReview ?? [];
  const requests = useWaitingRequests();
  const texts = useQuery({ queryKey: ["comm", "conversations", false], queryFn: () => get<ConversationView[]>("/api/communications/conversations"), enabled: can("calls.view"), refetchInterval: MINUTE });
  const calls = useQuery({ queryKey: ["calls", "unread"], queryFn: () => get<PhoneEventView[]>("/api/calls?filter=unread"), enabled: can("calls.view"), refetchInterval: MINUTE });
  const shelf = useQuery({ queryKey: ["inventory"], queryFn: () => get<InventoryShelf>("/api/inventory"), enabled: can("inventory.view"), refetchInterval: 5 * MINUTE });
  const security = useQuery({ queryKey: ["security"], queryFn: () => get<SecurityResponse>("/api/me/security") });
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff"), enabled: can("staff.manage") });
  const [everyone, setEveryone] = useState(false);
  if (!me) return null;

  // Visits: everyone's, or only mine when I have some today.
  const visits = (day.data?.appointments ?? [])
    .filter((a) => a.start.slice(0, 10) === today && isOpenStatus(a.status) && (a.context !== "mobile" || a.bookingStatus === "confirmed"))
    .sort((x, y) => x.start.localeCompare(y.start));
  const mine = visits.filter((a) => a.staffId === me.staff.id);
  const showMine = mine.length > 0 && !everyone;
  const shown = showMine ? mine : visits;
  const staffName = (id: string | null) => day.data?.staff.find((s) => s.id === id)?.name ?? "";
  const unconfirmed = (next.data?.appointments ?? []).filter(
    (a) => a.start.slice(0, 10) === tomorrow && a.status === "unconfirmed" && (a.context !== "mobile" || a.bookingStatus === "confirmed") && (everyoneToday || a.staffId === me.staff.id),
  );
  // Their own visits, today and the next six days, by day.
  const upcoming = (week.data?.appointments ?? [])
    .filter((a) => a.staffId === me.staff.id && isOpenStatus(a.status) && (a.context !== "mobile" || a.bookingStatus === "confirmed"))
    .sort((x, y) => x.start.localeCompare(y.start));
  const upcomingDays = [...new Set(upcoming.map((a) => a.start.slice(0, 10)))];
  // Event shifts they're on, not over yet.
  const nowIso = new Date().toISOString();
  const shifts = (month.data?.eventShifts ?? [])
    .filter((x, i, all) => x.staffId === me.staff.id && x.end > nowIso && all.findIndex((y) => y.occurrenceId === x.occurrenceId && y.staffId === x.staffId) === i)
    .sort((x, y) => x.start.localeCompare(y.start));

  const needReply = (texts.data ?? []).filter((c) => c.needsReply > 0);
  const callList = (calls.data ?? []).filter((c) => c.kind === "voicemail" || c.kind === "missed_call");

  const items = (shelf.data?.items ?? []).filter((i) => i.active);
  const low = items.filter((i) => i.low);
  const expired = items.flatMap((i) => i.lots.filter((l) => l.status === "expired" && l.remaining > 0).map((l) => ({ i, l })));
  const expiring = items.flatMap((i) => i.lots.filter((l) => l.status === "expiring" && l.remaining > 0).map((l) => ({ i, l })));

  const setup = [
    { show: !!security.data?.available, done: (security.data?.passkeys.length ?? 0) > 0, title: "Add a passkey", body: "Sign in with Face ID or Touch ID instead of typing a password.", to: "/account" },
    { show: can("staff.manage"), done: (staff.data?.length ?? 0) > 1, title: "Invite your team", body: "Add the people who work with you so they can sign in.", to: "/staff" },
    { show: can("settings.manage"), done: !!me.settings.logoUrl, title: "Add your logo", body: "Your logo appears in the menu and on booking pages.", to: "/settings/branding" },
  ].filter((t) => t.show && !t.done);

  const attention = unsigned.length + toReview.length + (requests.data?.length ?? 0) + unconfirmed.length;
  const loading = (booking && (everyoneToday ? day.isLoading : week.isLoading)) || (can("inventory.view") && shelf.isLoading);
  const nothing = !loading && !shown.length && !upcoming.length && !shifts.length && !attention && !needReply.length && !callList.length && !low.length && !expired.length && !expiring.length && !setup.length;

  return (
    <div className="flex max-w-5xl flex-col gap-6">
      <header>
        <h1 className="page-title">
          {greeting(me.clinic.timezone)}, {me.staff.firstName}
        </h1>
        <p className="mt-1 text-ink-soft">{longDate(today)}</p>
      </header>

      {loading && <Loading />}
      {nothing && <p className="rounded-[var(--radius-panel)] border border-line bg-white px-5 py-8 text-center text-lg text-ink-soft">All clear for today.</p>}

      {booking && !everyoneToday && (
        <Section
          title="Your upcoming visits"
          count={upcoming.length}
          action={
            <Link to="/front-desk" className="text-sm font-semibold text-brand">
              Front desk →
            </Link>
          }
        >
          {upcoming.length === 0 && !week.isLoading && <li className="px-5 py-4 text-sm text-muted">Nothing booked for you in the next 7 days.</li>}
          {upcomingDays.map((d) => (
            <li key={d}>
              <div className="bg-mist/50 px-5 py-1.5 text-xs font-bold tracking-wider text-muted uppercase">{d === today ? "Today" : d === tomorrow ? "Tomorrow" : dayHeading(d)}</div>
              <ul className="divide-y divide-mist">
                {upcoming
                  .filter((a) => a.start.slice(0, 10) === d)
                  .map((a) => (
                    <VisitRow key={a.id} a={a} nurse="" />
                  ))}
              </ul>
            </li>
          ))}
        </Section>
      )}

      {shifts.length > 0 && (
        <Section title="Your upcoming events" count={shifts.length}>
          {shifts.map((x) => (
            <Row key={x.occurrenceId} to={can("events.manage") ? `/events/${x.eventId}` : `/events/check-in/${x.occurrenceId}`} title={x.eventName} detail={`${x.start.slice(0, 10) === today ? "Today" : dayHeading(x.start.slice(0, 10))} · ${time12(x.start)}–${time12(x.end)}`} />
          ))}
        </Section>
      )}

      {booking && everyoneToday && shown.length > 0 && (
        <Section
          title={showMine ? "Your visits today" : "Visits today"}
          count={shown.length}
          action={
            mine.length > 0 && mine.length < visits.length ? (
              <button className="text-sm font-semibold text-brand" onClick={() => setEveryone(!everyone)}>
                {showMine ? `Everyone's (${visits.length})` : "Only mine"}
              </button>
            ) : (
              <Link to="/front-desk" className="text-sm font-semibold text-brand">
                Front desk →
              </Link>
            )
          }
        >
          {shown.map((a) => (
            <VisitRow key={a.id} a={a} nurse={staffName(a.staffId) || "No nurse"} />
          ))}
        </Section>
      )}

      {attention > 0 && (
        <Section title="Needs attention" count={attention}>
          {unsigned.map((c) => (
            <Row key={c.chartId ?? c.appointmentId} to={c.chartId ? `/charts/${c.chartId}` : `/visits/${c.appointmentId}`} title={`Chart to sign: ${c.clientName}`} detail={`${c.name} · ${c.serviceName} · ${shortDate(c.date)}`} tone="wait" />
          ))}
          {toReview.length > 0 && <Row to="/pending-charts" title={`${toReview.length} chart${toReview.length > 1 ? "s" : ""} waiting for your review`} detail={toReview.slice(0, 3).map((c) => c.clientName).join(", ")} tone="wait" />}
          {(requests.data ?? []).map((r) => (
            <Row key={r.bookingId} to={`/mobile-requests/${r.bookingId}`} title={`Mobile request: ${whenRequested(r)}`} detail={`${r.guests.map((g) => g.name).join(", ")} · ${r.address}`} tone="wait" />
          ))}
          {unconfirmed.length > 0 && (
            <Row
              // One visit: open it; several: tomorrow on the calendar.
              to={unconfirmed.length === 1 ? `/visits/${unconfirmed[0]!.id}` : `/calendar?date=${tomorrow}`}
              title={`${unconfirmed.length} visit${unconfirmed.length > 1 ? "s" : ""} tomorrow not confirmed yet`}
              detail={unconfirmed
                .slice(0, 4)
                .map((a) => `${time12(a.start)} ${displayName(a.client)}`)
                .join(", ")}
            />
          )}
        </Section>
      )}

      {(needReply.length > 0 || callList.length > 0) && (
        <Section title="Messages and calls" count={needReply.length + callList.length}>
          {needReply.slice(0, 5).map((c) => (
            <Row key={c.client?.id ?? c.from ?? c.last.at} to="/communication" title={`Text from ${c.client?.name ?? c.known?.name ?? c.from ?? "unknown"}`} detail={c.last.body} />
          ))}
          {callList.slice(0, 5).map((c) => (
            <Row
              key={c.id}
              to="/communication/phone"
              title={`${c.kind === "voicemail" ? "Voicemail" : "Missed call"} from ${c.client?.name ?? c.known?.name ?? c.callerName ?? c.from}`}
              detail={c.transcript ?? new Date(c.at).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}
            />
          ))}
          {needReply.length + callList.length > 10 && <Row to="/communication" title="See all" />}
        </Section>
      )}

      {(low.length > 0 || expired.length > 0 || expiring.length > 0) && (
        <Section title="Inventory" count={low.length + expired.length + expiring.length}>
          {expired.map(({ i, l }) => (
            <Row key={l.id} to={`/inventory/${kindTab(i)}`} title={`Expired, still on the shelf: ${i.name}`} detail={`Lot ${l.lotNumber} · ${l.expiresOn ?? ""} · waste it to take it off the count`} tone="danger" />
          ))}
          {low.map((i) => (
            <Row
              key={i.formularyItemId}
              to={`/inventory/${kindTab(i)}`}
              title={`Low stock: ${i.name}`}
              detail={`${formatVials(i.usable, i.lots.find((l) => l.perVial)?.perVial ?? perVial(i.doseUnit, i.strengthPerMl, i.vialSizeMl), i.doseUnit, i.kind)} left (low at ${i.reorderAt})`}
              tone="danger"
            />
          ))}
          {expiring.map(({ i, l }) => (
            <Row key={l.id} to={`/inventory/${kindTab(i)}`} title={`Expiring soon: ${i.name}`} detail={`Lot ${l.lotNumber} · ${l.expiresOn ?? ""}`} tone="wait" />
          ))}
        </Section>
      )}

      {setup.length > 0 && (
        <Section title="Getting set up" count={setup.length}>
          {setup.map((t) => (
            <Row key={t.title} to={t.to} title={t.title} detail={t.body} />
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, count, action, children }: { title: string; count: number; action?: ReactNode; children: ReactNode }) {
  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-mist px-5 py-3">
        <h2 className="font-semibold">
          {title} <span className="text-muted">({count})</span>
        </h2>
        {action}
      </div>
      <ul className="divide-y divide-mist">{children}</ul>
    </Panel>
  );
}

function Row({ to, title, detail, tone }: { to: string; title: string; detail?: string; tone?: "wait" | "danger" }) {
  return (
    <li>
      <Link to={to} className="flex items-start gap-3 px-5 py-3 hover:bg-mist/60">
        <span className={cx("mt-1.5 size-2 shrink-0 rounded-full", tone === "danger" ? "bg-danger" : tone === "wait" ? "bg-wait" : "bg-line")} aria-hidden />
        <span className="min-w-0">
          <span className="block font-semibold">{title}</span>
          {detail && <span className="block truncate text-sm text-muted">{detail}</span>}
        </span>
      </Link>
    </li>
  );
}

function VisitRow({ a, nurse }: { a: AppointmentView; nurse: string }) {
  const where = a.context === "mobile" ? `Mobile${a.address ? ` · ${a.address}` : ""}` : a.context === "event" ? (a.eventName ?? "Event") : null;
  return (
    <li>
      <Link to={`/visits/${a.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 hover:bg-mist/60">
        <span className="w-20 font-semibold tabular-nums">{time12(a.start)}</span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">
            {displayName(a.client)} <span className="font-normal text-ink-soft">· {a.serviceName}{a.addOns.length > 0 ? ` + ${a.addOns.length}` : ""}</span>
          </span>
          {(nurse || where) && (
            <span className="block truncate text-sm text-muted">
              {nurse}
              {nurse && where ? " · " : ""}
              {where ?? ""}
            </span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <FormsBadge a={a} compact />
          <GfeBadge g={a.gfe} compact />
          <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABELS[a.status]}</Badge>
        </span>
      </Link>
    </li>
  );
}
